import { useEffect, useState } from "react";
import { ArrowDown, ArrowUp, Eye, EyeOff, Loader2, Plus, Save, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { fmtDate } from "@/lib/roles";
import { refreshSiteContent } from "@/hooks/useSiteContent";
import { AppShell } from "@/components/app/AppShell";
import { CERT_ICONS } from "@/components/marketing/CertificationGrid";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

const lbl = "font-mono-label text-[9px] font-semibold text-oath-muted";
const CONTACT_FIELDS = [
  ["support_email", "SUPPORT EMAIL"], ["legal_email", "LEGAL EMAIL"], ["rights_email", "SUBJECT-RIGHTS EMAIL"],
  ["phone", "PHONE"], ["address", "MAILING ADDRESS"],
];

const CertRow = ({ c, i, total, update, move, remove }) => (
  <div className={`rounded-2xl border p-4 ${c.visible ? "border-gray-200 bg-white" : "border-dashed border-gray-300 bg-gray-50"}`} data-testid={`cert-row-${i}`}>
    <div className="flex flex-wrap items-center gap-2">
      <select value={c.icon} onChange={(e) => update(i, "icon", e.target.value)} className="h-9 rounded-md border border-gray-200 bg-white px-2 text-sm" data-testid={`cert-icon-${i}`}>
        {Object.keys(CERT_ICONS).map((k) => <option key={k} value={k}>{k}</option>)}
      </select>
      <Input value={c.title} onChange={(e) => update(i, "title", e.target.value)} className="h-9 min-w-[200px] flex-1 font-medium" data-testid={`cert-title-${i}`} />
      <button type="button" onClick={() => update(i, "visible", !c.visible)} data-testid={`cert-visible-${i}`} data-state={c.visible ? "on" : "off"}
        className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold ${c.visible ? "bg-emerald-600 text-white" : "border border-gray-300 text-oath-secondary"}`}>
        {c.visible ? <Eye className="h-3.5 w-3.5" /> : <EyeOff className="h-3.5 w-3.5" />} {c.visible ? "Shown" : "Hidden"}
      </button>
      <button type="button" disabled={i === 0} onClick={() => move(i, -1)} className="rounded p-1.5 hover:bg-gray-100 disabled:opacity-30" data-testid={`cert-up-${i}`}><ArrowUp className="h-4 w-4" /></button>
      <button type="button" disabled={i === total - 1} onClick={() => move(i, 1)} className="rounded p-1.5 hover:bg-gray-100 disabled:opacity-30" data-testid={`cert-down-${i}`}><ArrowDown className="h-4 w-4" /></button>
      <button type="button" onClick={() => remove(i)} className="rounded p-1.5 text-red-600 hover:bg-red-50" data-testid={`cert-remove-${i}`}><Trash2 className="h-4 w-4" /></button>
    </div>
    <Textarea rows={2} value={c.desc} onChange={(e) => update(i, "desc", e.target.value)} className="mt-2 text-sm" data-testid={`cert-desc-${i}`} />
  </div>
);

const StatsEditor = ({ title, hint, rows, onChange, testid }) => {
  const upd = (i, k, v) => onChange(rows.map((r, j) => (j === i ? { ...r, [k]: v } : r)));
  return (
    <section className="mt-6 rounded-3xl border border-gray-200 bg-white p-6" data-testid={testid}>
      <h2 className="font-display text-lg font-bold text-oath-ink">{title}</h2>
      <p className="mt-1 text-sm text-oath-secondary">{hint}</p>
      <div className="mt-5 grid gap-3">
        {rows.map((r, i) => (
          <div key={i} className="grid gap-2 rounded-2xl border border-gray-200 p-3 sm:grid-cols-[70px_110px_70px_90px_1fr]" data-testid={`${testid}-row-${i}`}>
            <Input placeholder="Prefix" value={r.prefix} onChange={(e) => upd(i, "prefix", e.target.value)} data-testid={`${testid}-prefix-${i}`} />
            <Input type="number" step="any" value={r.value} onChange={(e) => upd(i, "value", e.target.value === "" ? "" : Number(e.target.value))} data-testid={`${testid}-value-${i}`} />
            <Input placeholder="Suffix" value={r.suffix} onChange={(e) => upd(i, "suffix", e.target.value)} data-testid={`${testid}-suffix-${i}`} />
            <select value={r.decimals} onChange={(e) => upd(i, "decimals", Number(e.target.value))} className="h-10 rounded-md border border-gray-200 bg-white px-2 text-sm" data-testid={`${testid}-decimals-${i}`}>
              {[0, 1, 2].map((d) => <option key={d} value={d}>{d} dec.</option>)}
            </select>
            <Input placeholder="Caption" value={r.caption} onChange={(e) => upd(i, "caption", e.target.value)} data-testid={`${testid}-caption-${i}`} />
            {"note" in r && testid === "content-problem" && (
              <Textarea rows={2} value={r.note} onChange={(e) => upd(i, "note", e.target.value)} className="text-sm sm:col-span-5" data-testid={`${testid}-note-${i}`} />
            )}
          </div>
        ))}
      </div>
    </section>
  );
};

export default function AdminContent() {
  const [doc, setDoc] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let active = true;
    api.get("/admin/content").then(({ data }) => active && setDoc((d) => d || data)).catch((e) => toast.error(formatApiError(e)));
    return () => {
      active = false;
    };
  }, []);

  if (!doc) return <AppShell eyebrow="— SUPER ADMIN" title="Site content"><Loader2 className="h-5 w-5 animate-spin text-oath-muted" /></AppShell>;

  const certs = doc.certifications;
  const setCerts = (next) => setDoc({ ...doc, certifications: next });
  const update = (i, k, v) => setCerts(certs.map((c, j) => (j === i ? { ...c, [k]: v } : c)));
  const move = (i, d) => {
    const next = [...certs];
    [next[i], next[i + d]] = [next[i + d], next[i]];
    setCerts(next);
  };
  const remove = (i) => setCerts(certs.filter((_, j) => j !== i));
  const add = () => setCerts([...certs, { id: `item-${Date.now().toString(36)}`, title: "New certification", desc: "Describe what this certification covers.", icon: "badge", visible: false }]);

  const save = async () => {
    if ([...doc.stats, ...doc.problem_stats].some((s) => s.value === "" || Number.isNaN(Number(s.value)))) {
      toast.error("Every figure needs a number.");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.put("/admin/content", { contact: doc.contact, certifications: certs, stats: doc.stats, problem_stats: doc.problem_stats });
      setDoc(data);
      await refreshSiteContent();
      toast.success("Website updated");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const actions = (
    <button onClick={save} disabled={saving} data-testid="content-save-button"
      className="inline-flex items-center gap-2 rounded-full bg-oath-ink px-5 py-2.5 font-mono-label text-[11px] font-semibold text-white hover:bg-black disabled:opacity-60">
      {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} SAVE & PUBLISH
    </button>
  );

  return (
    <AppShell eyebrow="— SUPER ADMIN" title="Site content" actions={actions}>
      {doc.updated_at && <p className="-mt-4 mb-6 text-xs text-oath-muted" data-testid="content-updated-at">Last published {fmtDate(doc.updated_at)}</p>}
      <section className="rounded-3xl border border-gray-200 bg-white p-6" data-testid="content-contact-section">
        <h2 className="font-display text-lg font-bold text-oath-ink">Contact details</h2>
        <p className="mt-1 text-sm text-oath-secondary">Shown in the footer, sign-in, subject-rights and messaging pages. Leave phone or address blank to hide them.</p>
        <div className="mt-5 grid gap-4 sm:grid-cols-2">
          {CONTACT_FIELDS.map(([k, label]) => (
            <div key={k} className={k === "address" ? "sm:col-span-2" : ""}>
              <label className={lbl}>{label}</label>
              <Input value={doc.contact[k] || ""} onChange={(e) => setDoc({ ...doc, contact: { ...doc.contact, [k]: e.target.value } })} className="mt-2" data-testid={`content-${k}-input`} />
            </div>
          ))}
        </div>
      </section>
      <StatsEditor title="Homepage — the problem" hint="Three figures in the “The problem” section. Numbers count up when scrolled into view." rows={doc.problem_stats}
        onChange={(r) => setDoc({ ...doc, problem_stats: r })} testid="content-problem" />
      <StatsEditor title="Homepage — coverage & scale" hint="Four figures next to the coverage map." rows={doc.stats}
        onChange={(r) => setDoc({ ...doc, stats: r })} testid="content-stats" />
      <section className="mt-6 rounded-3xl border border-gray-200 bg-white p-6" data-testid="content-certs-section">
        <h2 className="font-display text-lg font-bold text-oath-ink">Certifications & compliance badges</h2>
        <p className="mt-1 text-sm text-oath-secondary">Only items marked Shown appear on the homepage and in the footer. Show a certification only once it is confirmed.</p>
        <div className="mt-5 grid gap-3">
          {certs.map((c, i) => <CertRow key={c.id} c={c} i={i} total={certs.length} update={update} move={move} remove={remove} />)}
        </div>
        <button type="button" onClick={add} data-testid="cert-add-button"
          className="mt-4 inline-flex items-center gap-1.5 rounded-full border border-dashed border-gray-300 px-4 py-2 text-xs text-oath-ink hover:border-gray-400">
          <Plus className="h-3.5 w-3.5" /> Add item
        </button>
      </section>
    </AppShell>
  );
}
